import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { DEFAULT_ACCEPTED_FILES } from '~/common/constants/files';
import { trackSegment } from '~/components/SegmentAnalytics';
import EcModal from '~/components/Shared/EcModal/EcModal';
import { ContentContainer } from '~/eds/src/components/ContentContainer/ContentContainer';
import { FileInput } from '~/eds/src/components/FileInput/FileInput';
import { Files } from '~/eds/src/components/Files/Files';
import { FolderInput } from '~/eds/src/components/FolderInput/FolderInput';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Panel } from '~/eds/src/components/Panel/Panel';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { api } from '~/redux/api/createApi';
import { coerceRtkqError } from '~/redux/api/utils';
import { slices } from '~/redux/slices';
import { MODAL_FOLDER_TREE } from '~/types/modal.types';
import { Nullable } from '~/types/Nullable';
import { getClientInfo, getUserClientInfo } from '~/utils/user';

import { FormFields } from './FormFields';
import { FormFieldConfig, Location, UploadField } from './types';

export const UploadDocumentsPanel = ({
  onRemoveFile,
}: {
  onRemoveFile: (removedFile: File, removedFileIndex?: number) => void;
}) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { selectedFiles, configuration } = useSelector(
    slices.upload.selectors.selectUploadConfiguration,
  );

  const { enableSettings, fields } = configuration;

  const { toast } = useToast();
  const user = useCurrentUser();

  const [locationModal, setLocationModal] = useState<string | null>(null);

  const [_hasUploadPermission, setHasUploadPermission] = useState(null);

  const [uploadLocation, setUploadLocation] =
    useState<Nullable<Location>>(null);

  const formFields: FormFieldConfig[] =
    user.client_config.upload_form_fields.map(
      ({ allow_new_options, ...field }) => ({
        ...field,
        allowNewOptions: allow_new_options,
      }),
    );

  const {
    data: folderTree,
    isFetching: isLoadingFolderTree,
    error: folderTreeError,
  } = api.endpoints.getFolderTree.useQuery(undefined);

  const [accessFolder] = api.endpoints.getFolderVisibility.useLazyQuery();
  const [uploadFiles] = api.endpoints.addUploadFiles.useMutation();

  const handleOnHide = () => {
    setUploadLocation(null);
    dispatch(slices.upload.actions.resetFileConfiguration());
  };

  const handleLocationChange = (location: Location) => {
    const folderId = location.id;
    accessFolder({ folderId }).then((res: any) => {
      if (res.error) {
        toast({
          message: res.error.message,
          status: 'danger',
        });
      } else {
        setUploadLocation(location);
        setHasUploadPermission(res.data.canModifyFolder);
      }
    });
  };

  const handleOnChange = (formData: UploadField) => {
    const { fieldId, value } = formData;

    const fieldIndex = fields.findIndex((field) => field.fieldId === fieldId);

    if ((Array.isArray(value) && value.length === 0) || !value) {
      dispatch(
        slices.upload.actions.setFields(
          fields.filter((_, index) => index !== fieldIndex),
        ),
      );
    } else if (fieldIndex === -1) {
      dispatch(
        slices.upload.actions.setFields([...fields, { fieldId, value }]),
      );
    } else {
      dispatch(
        slices.upload.actions.setFields(
          fields.map((item, index) =>
            index === fieldIndex ? { fieldId, value } : item,
          ),
        ),
      );
    }
  };

  const locationName = uploadLocation
    ? uploadLocation.name
    : (folderTree?.name ?? '');

  const folderId = uploadLocation
    ? uploadLocation.id
    : folderTree?.id
      ? folderTree.id
      : '2';

  const extendedFiles =
    selectedFiles && selectedFiles.length > 0
      ? selectedFiles.map((file) => {
          //Remove this 2, but confirm how to prevent foldertree from undefined?
          // @ts-ignore folderId is not a property of File
          file.folderId = uploadLocation
            ? uploadLocation.id
            : folderTree?.id
              ? folderTree.id
              : '2';
          return file;
        })
      : [];

  const children = (
    <>
      {enableSettings ? (
        <Layout preset="sections">
          {!folderTreeError && (
            <FolderInput
              folder={{
                name: locationName,
                path: uploadLocation?.path ?? '',
                isConcise: true,
              }}
              onChange={() => setLocationModal(MODAL_FOLDER_TREE)}
              isLoading={isLoadingFolderTree}
            />
          )}
          <ContentContainer
            placeholderContent={
              !user.client_config.upload_form_fields
                ? {
                    message: intl.formatMessage({
                      id: 'UploadDocumentsPanel.formFieldsNotAvailable',
                      defaultMessage: 'FormFields are not available',
                      description: 'Message when form fields are not available',
                    }),
                  }
                : undefined
            }
          >
            <FormFields
              formFields={formFields}
              fields={fields}
              onChange={handleOnChange}
            />
          </ContentContainer>
        </Layout>
      ) : (
        <Layout preset="sections">
          <FileInput
            accept={DEFAULT_ACCEPTED_FILES}
            isMulti={true}
            name={intl.formatMessage({
              id: 'UploadDocumentsPanel.uploadFilesDropzoneName',
              defaultMessage: 'Upload Files Dropzone',
              description: 'Name for upload files dropzone input',
            })}
            onChange={(uploadedFiles) => {
              dispatch(
                slices.upload.actions.setSelectedFiles(
                  uploadedFiles
                    ? [
                        ...uploadedFiles,
                        ...uploadedFiles.filter(
                          (file) =>
                            !selectedFiles.some((f) => f.name === file.name),
                        ),
                      ]
                    : [],
                ),
              );
            }}
            placeholder={intl.formatMessage({
              id: 'UploadDocumentsPanel.dragDocumentsPlaceholder',
              defaultMessage: 'Drag document(s) here to upload them.',
              description: 'Placeholder text for file upload dropzone',
            })}
            value={null}
          />
          <Files
            files={selectedFiles}
            onRemoveFile={onRemoveFile}
            title={intl.formatMessage({
              id: 'UploadDocumentsPanel.uploadFilesTitle',
              defaultMessage: 'Upload Files',
              description: 'Title for upload files section',
            })}
          />
        </Layout>
      )}
      {locationModal === MODAL_FOLDER_TREE && (
        <EcModal
          modalType={MODAL_FOLDER_TREE}
          width="540px"
          title={intl.formatMessage({
            id: 'UploadDocumentsPanel.chooseLocationTitle',
            defaultMessage: 'Choose Location',
            description: 'Modal title for choosing upload location',
          })}
          uploadLocation={uploadLocation}
          uploadLocationPath={uploadLocation?.path ? uploadLocation.path : []}
          folderIdsSelected={[]}
          handleUploadLocationChange={handleLocationChange}
          confirmButtonText={intl.formatMessage({
            id: 'UploadDocumentsPanel.chooseLocationButton',
            defaultMessage: 'Choose Location',
            description: 'Button text for choosing location',
          })}
          hideModal={() => {
            setLocationModal(null);
          }}
          enableSearch={true}
        />
      )}
    </>
  );
  const hasPermissionToUpload = false;

  const disableTooltip = !enableSettings
    ? intl.formatMessage({
        id: 'UploadDocumentsPanel.selectNextTooltip',
        defaultMessage: 'Please select next to add your folder location',
        description: 'Tooltip when settings are not enabled',
      })
    : !hasPermissionToUpload
      ? intl.formatMessage({
          id: 'UploadDocumentsPanel.selectFolderTooltip',
          defaultMessage: ' Please select a folder you have access to.',
          description: 'Tooltip when user lacks upload permission',
        })
      : undefined;

  return (
    <Panel
      width="m"
      children={children}
      position="right"
      title={intl.formatMessage({
        id: 'UploadDocumentsPanel.title',
        defaultMessage: 'Upload',
        description: 'Panel title for document upload',
      })}
      hidden={{
        placement: 'left',
        onHide: handleOnHide,
      }}
      footer={{
        actions: [
          {
            text: intl.formatMessage({
              id: 'UploadDocumentsPanel.cancelButton',
              defaultMessage: 'Cancel',
              description: 'Cancel button text',
            }),
            onClick: handleOnHide,
          },
          {
            text: intl.formatMessage({
              id: 'UploadDocumentsPanel.nextButton',
              defaultMessage: 'Next',
              description: 'Next button text',
            }),
            onClick: () => {
              dispatch(
                slices.upload.actions.setConfiguration({
                  ...configuration,
                  enableSettings: true,
                }),
              );
            },
            disabled: enableSettings,
          },
          {
            text: intl.formatMessage({
              id: 'UploadDocumentsPanel.uploadButton',
              defaultMessage: 'Upload',
              description: 'Upload button text',
            }),
            disabled: Boolean(disableTooltip),
            tooltip: disableTooltip,
            onClick: () => {
              trackSegment('User Uploads Document', {
                fileNames: selectedFiles.map((file) => file.name),
                groupId: getClientInfo(user),
                userId: getUserClientInfo(user),
              });
              uploadFiles({
                files: extendedFiles,
                folderId: String(folderId),
                uploadFormFields: fields,
              }).then((res) => {
                if ('error' in res) {
                  toast({
                    message: coerceRtkqError(res.error)?.message,
                    status: 'danger',
                  });
                } else {
                  toast({
                    message: intl.formatMessage({
                      id: 'UploadDocumentsPanel.uploadSuccessMessage',
                      defaultMessage: 'Files uploaded successfully',
                      description: 'Success message for file upload',
                    }),
                    status: 'success',
                  });
                }
              });
              handleOnHide();
            },
            level: 'primary',
          },
        ],
      }}
    />
  );
};
